import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import type { MeDto } from '@tiffinify/contracts';
import { BtnPrimary, BtnSecondary, Card, ErrorText, Icon, Logo, TaskTitle } from '../components/ui';
import { get, post } from '../lib/api';
import { errorMessage } from '../lib/format';
import { useEnterKitchen } from '../lib/queries';

/*
 * A kitchen the owner signed up for themselves waits for the Tiffinify team (the server refuses everything else in
 * it until then). This is all they see meanwhile: where things stand, and a way to check again or sign out.
 */
export function Waiting({ me }: { me: MeDto }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const enter = useEnterKitchen();
  const first = me.user.name.split(' ')[0] ?? me.user.name;
  const rejected = me.kitchen.status === 'rejected';
  const other = me.kitchens.find((k) => k.id !== me.kitchen.id);

  // Once approved, start with setting the kitchen up.
  const check = useMutation({
    mutationFn: () => get<MeDto>('/me'),
    onSuccess: (fresh) => {
      qc.setQueryData(['me'], fresh);
      if (fresh.kitchen.status === 'approved') nav('/settings?setup=1', { replace: true });
    },
  });
  // Look again every minute while the screen is open, so an approval shows without a tap.
  useEffect(() => {
    if (rejected) return;
    const t = setInterval(() => check.mutate(), 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejected]);

  const switchTo = useMutation({ mutationFn: (kitchenId: string) => post<MeDto>('/auth/switch', { kitchenId }), onSuccess: enter });
  const logout = async () => { await post('/auth/logout'); qc.clear(); nav('/welcome', { replace: true }); };

  const steps: Array<{ done: boolean; now?: boolean; title: string; sub: string }> = [
    { done: true, title: `You signed up ${me.kitchen.name}`, sub: 'Your account and kitchen are saved.' },
    { done: false, now: true, title: 'The Tiffinify team checks it', sub: 'We verify every new kitchen, usually within a day.' },
    { done: false, title: 'You get an email', sub: me.user.email ? `At ${me.user.email}, as soon as it’s ready.` : 'As soon as it’s ready.' },
  ];

  return (
    <main className="main task waiting">
      <div className="waiting-logo">
        <Logo size={18} />
      </div>
      <div className="waiting-mark" aria-hidden="true"><Icon name={rejected ? 'info' : 'pot'} size={30} sw={2} /></div>
      {rejected ? (
        <>
          <TaskTitle title={`We couldn’t verify ${me.kitchen.name}`} sub="The Tiffinify team could not approve this kitchen yet, so it can’t be used for now." />
          <Card pad>
            <p className="waiting-note">We sent you an email about it. <b>Reply to that email</b> to tell us more about your kitchen, and we’ll take another look.</p>
          </Card>
        </>
      ) : (
        <>
          <TaskTitle title={`Almost there, ${first}!`} sub={`${me.kitchen.name} is waiting for approval from the Tiffinify team.`} />
          <Card pad>
            <ol className="waiting-steps">
              {steps.map((s) => (
                <li key={s.title} className={s.done ? 'done' : s.now ? 'now' : ''}>
                  <span className="dot" aria-hidden="true">{s.done ? <Icon name="check" size={14} sw={3} /> : null}</span>
                  <span className="grow"><b>{s.title}</b><small>{s.sub}</small></span>
                </li>
              ))}
            </ol>
          </Card>
        </>
      )}

      <ErrorText>{check.error ? errorMessage(check.error) : switchTo.error ? errorMessage(switchTo.error) : ''}</ErrorText>
      {check.isSuccess && check.data.kitchen.status !== 'approved' && !rejected && <p className="waiting-note" role="status">Not approved yet. We’ll keep checking while this page is open.</p>}
      {!rejected && <BtnPrimary busy={check.isPending} label={check.isPending ? 'Checking…' : 'Check again'} icon="refresh" onClick={() => check.mutate()} />}
      {other && <BtnSecondary busy={switchTo.isPending} label={`Open ${other.name}`} onClick={() => switchTo.mutate(other.id)} />}
      <BtnSecondary label="Sign out" onClick={logout} />
    </main>
  );
}
